import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded text-sm font-medium ' +
    'transition-colors active:translate-y-px disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-ink hover:bg-primary/90',
        secondary: 'border border-line bg-accent text-accent-ink hover:bg-accent/70',
        outline: 'border border-line bg-transparent text-ink hover:bg-surface-sunken',
        ghost: 'bg-transparent text-ink hover:bg-surface-sunken',
        danger: 'bg-danger text-white hover:bg-danger/90',
      },
      size: {
        sm: 'h-[26px] px-2.5',
        md: 'h-[30px] px-3',
        icon: 'h-[30px] w-[30px] p-0',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled ?? loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {/* Slot accepts exactly one child, so `asChild` opts out of the injected spinner. */}
        {loading && !asChild ? (
          <>
            <Loader2 className="size-3 shrink-0 animate-spin" aria-hidden="true" />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
